import { Stack, Tooltip, Typography, useTheme } from '@mui/material';
import { useAppTFunction } from '@retail/app/i18n';
import { ConditionsNew, MarkupConditionNew } from '@retail/calculus/types';
import { getPriceSuggestionType } from '@retail/products/utils';
import { CheckIcon, InheritedIcon } from '@shared/custom-icons';
import { ConditionTooltipInfo } from '../ConditionTooltipInfo';
import { useCallback, useMemo } from 'react';
import { PriceSuggestionPriority } from '@retail/products/types';

interface Props {
  condition: ConditionsNew;
  draftedMarkup?: MarkupConditionNew;
  replacedMarkup?: MarkupConditionNew;
}

export function SalesPriceConditionValue({ condition, draftedMarkup, replacedMarkup }: Props) {
  const t = useAppTFunction();
  const theme = useTheme();
  const palette = theme.vars.palette;

  const getMarkupCondition = useCallback(
    (markup?: MarkupConditionNew) =>
      `${t('markup.markup')}: ${
        markup?.coverageDegree != null
          ? `${markup?.coverageDegree.toFixed(0)}%`
          : markup?.salesFactor
          ? markup?.salesFactor
          : ''
      }`,
    [t]
  );

  const markupValue = replacedMarkup ?? condition.markupCondition;

  const priceSourcePriorities: PriceSuggestionPriority[] = useMemo(() => {
    const prio = condition.priceSourceCondition?.priorities;

    if (prio?.map((prio) => prio.priceSuggestionType).includes('MARKUP')) {
      return prio;
    } else {
      return [
        ...(prio ?? []),
        { priceSuggestionType: 'MARKUP', priority: (prio?.length ?? 0) + 1 },
      ];
    }
  }, [condition.priceSourceCondition?.priorities]);

  return (
    <Stack display="flex" flexGrow={1}>
      {priceSourcePriorities?.map((prio) => {
        return (
          <Stack
            key={prio.priceSuggestionType}
            flexDirection="row"
            alignItems="center"
            gap={0.5}
            px={2}
          >
            <Typography variant="subtitle2">{prio.priority}.</Typography>

            {prio.priceSuggestionType === 'MARKUP' && draftedMarkup ? (
              <Stack flexDirection="row" alignItems="center" gap={0.5}>
                <Typography variant="body2">{getMarkupCondition(draftedMarkup)}</Typography>
                <Tooltip title={t('calculus.draftedMarkup')}>
                  <Stack>
                    <CheckIcon scale={0.6} color={palette.secondary.main} />
                  </Stack>
                </Tooltip>
              </Stack>
            ) : prio.priceSuggestionType === 'MARKUP' && !draftedMarkup ? (
              <Stack flexDirection="row" alignItems="center" gap={0.5}>
                <Typography variant="body2">{getMarkupCondition(markupValue)}</Typography>
                {markupValue?.isInherited &&
                  markupValue.inheritedBinding?.bindingType !== condition.bindingType && (
                    <Tooltip
                      title={
                        <ConditionTooltipInfo
                          title={t('calculus.inheritedMarkup')}
                          bindingType={markupValue.inheritedBinding?.bindingType}
                          bindingTarget={`${markupValue.inheritedBinding?.bindingTargetName}, ${markupValue.inheritedBinding?.bindingTargetId}`}
                        />
                      }
                    >
                      <Stack>
                        <InheritedIcon
                          sx={{ transform: 'scale(0.8)' }}
                          fill={palette.text.secondary}
                        />
                      </Stack>
                    </Tooltip>
                  )}
              </Stack>
            ) : (
              <Typography variant="body2">
                {getPriceSuggestionType(t, prio.priceSuggestionType)}{' '}
              </Typography>
            )}
          </Stack>
        );
      })}
    </Stack>
  );
}
